import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    isActive?: boolean;
}

export interface AddressType {
    id: number;
    typeName: string;
}

export interface ContactInfoType {
    id: number;
    typeName: string;
}

export interface Address {
    id: number;
    addressType: AddressType;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    postalCode?: string;
    city?: string;
    country?: string;
}

export interface ContactInfo {
    id: number;
    contactInfoType: ContactInfoType;
    value?: string;
}

export interface Pricelist {
    id: number;
    pricelistName: string;
}

export interface LocationType {
    id: number;
    description: string;
}

export interface LocationCategory {
    id: number;
    category: string;
}

export interface SalesChannel {
    id: number;
    salesChannelCode?: string;
    salesChannelDescription?: string;
}

export interface ResellerCustomer {
    id: number;
    firstName?: string;
    lastName?: string;
    socialSecurityNumber?: string;
}

export interface Location {
    id: number;
    locationName: string;
    locationNumber: number;
    isActive: boolean;
    shouldGenerateAutoOrders: boolean;
    locationType?: LocationType | null;
    locationCategory?: LocationCategory | null;
    salesChannel?: SalesChannel | null;
    resellerCustomer?: ResellerCustomer | null;
    resellerCostPriceFactor?: number;
    storeManager?: User | null;
    squareMeters: number;
    startDate?: string | null;
    organizationNumber?: string | null;
    resellerPriceList?: Pricelist | null;
    addresses: Address[];
    contactInfos: ContactInfo[];
    users: User[];
}

export async function getLocation(id: number): Promise<Location> {
    const result = await rms2Fetch<Location>(`locations/${id}/detail`);
    if (!result) throw new Error('Empty response for getLocation');
    return result;
}

export async function updateLocation(loc: Location): Promise<Location> {
    const result = await rms2Fetch<Location>(`locations/${loc.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            locationName: loc.locationName,
            isActive: loc.isActive,
            shouldGenerateAutoOrders: loc.shouldGenerateAutoOrders,
            storeManagerId: loc.storeManager?.id ?? null,
            squareMeters: loc.squareMeters,
            resellerPriceListId: loc.resellerPriceList?.id ?? null,
            addresses: loc.addresses.map((a) => ({
                id: a.id,
                addressTypeId: a.addressType.id,
                addressLine1: a.addressLine1,
                addressLine2: a.addressLine2,
                addressLine3: a.addressLine3,
                postalCode: a.postalCode,
                city: a.city,
                country: a.country
            })),
            contactInfos: loc.contactInfos.map((c) => ({
                id: c.id,
                contactInfoTypeId: c.contactInfoType.id,
                value: c.value
            }))
        })
    });
    if (!result) throw new Error('Empty response for updateLocation');
    return result;
}

export async function getUsers(): Promise<User[]> {
    const response = await rms2Fetch<{ users: User[] }>('activeusers');
    return response?.users ?? [];
}

export async function getAddressTypes(): Promise<AddressType[]> {
    const response = await rms2Fetch<{ addressTypes: AddressType[] }>('addresstypes');
    return response?.addressTypes ?? [];
}

export async function getContactInfoTypes(): Promise<ContactInfoType[]> {
    const response = await rms2Fetch<{ contactInfoTypes: ContactInfoType[] }>('contactinfotypes');
    return response?.contactInfoTypes ?? [];
}

export async function getUserLocations(): Promise<number[]> {
    const response = await rms2Fetch<{ locationIds: number[] }>('locations/user/locations');
    return response?.locationIds ?? [];
}

export async function getPricelists(): Promise<Pricelist[]> {
    const response = await rms2Fetch<{ priceLists: Pricelist[] }>('pricelists');
    return response?.priceLists ?? [];
}
